import { useId } from 'react';
import { Calculator } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useSupportedCurrencies } from '@/api/publishing';
import { CURRENCY_NOT_SET_LABEL } from '@/lib/format-money';

interface PricingFieldsProps {
  pricePerHour?: number;
  pricePerDay?: number;
  pricePerWeek?: number;
  /** Null when the partner sent no currency for these prices. */
  currency: string | null;
  priceOnRequest?: boolean;
  onChange: (pricing: {
    pricePerHour?: number | null;
    pricePerDay?: number | null;
    pricePerWeek?: number | null;
    currency?: string;
    priceOnRequest?: boolean;
  }) => void;
  /**
   * When true, prices are auto-calculated from partner pricing and cannot be edited.
   */
  readOnly?: boolean;
}

/** Shown while the list loads or when it cannot be fetched. */
const FALLBACK_CURRENCY_CODES = ['EUR', 'USD'];

const CURRENCY_SYMBOLS: Record<string, string> = { EUR: '€', USD: '$', GBP: '£' };

function currencyLabel(code: string): string {
  const symbol = CURRENCY_SYMBOLS[code];
  return symbol ? `${code} (${symbol})` : code;
}

/**
 * The codes to offer: the publishable list (or the fallback), plus the stored
 * value when the list lacks it, so an existing enrichment never renders a
 * blank selector. Such a value is refused at publish until it is changed.
 */
export function currencyOptions(available: readonly string[] | undefined, current: string | null): string[] {
  const codes = available && available.length > 0 ? [...available] : [...FALLBACK_CURRENCY_CODES];
  return current && !codes.includes(current) ? [...codes, current] : codes;
}

export function PricingFields({
  pricePerHour,
  pricePerDay,
  pricePerWeek,
  currency,
  priceOnRequest = false,
  onChange,
  readOnly = false,
}: PricingFieldsProps): JSX.Element {
  const { data: supportedCurrencies, isError: currenciesFailed } = useSupportedCurrencies();
  const options = currencyOptions(supportedCurrencies?.map((c) => c.code), currency);
  // Links the unknown-currency note to the selector for screen readers.
  const unknownNoteId = useId();

  const handlePriceChange = (
    field: 'pricePerHour' | 'pricePerDay' | 'pricePerWeek',
    value: string
  ) => {
    if (readOnly) return;
    const numValue = value ? parseFloat(value) : null;
    onChange({ [field]: numValue });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="text-base font-medium">Pricing</Label>
        <Select
          value={currency ?? ''}
          onValueChange={(value) => { if (!readOnly) onChange({ currency: value }); }}
          disabled={readOnly}
        >
          <SelectTrigger
            className={currency === null ? 'w-40' : 'w-32'}
            data-testid="currency-select"
            aria-describedby={currency === null ? unknownNoteId : undefined}
          >
            <SelectValue placeholder={CURRENCY_NOT_SET_LABEL} />
          </SelectTrigger>
          <SelectContent>
            {options.map((code) => (
              <SelectItem key={code} value={code}>
                {currencyLabel(code)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* The live region is always mounted so screen readers announce the note when it appears. */}
      <div role="status" data-testid="currency-fallback-status">
        {currenciesFailed && (
          <p className="text-xs text-muted-foreground" data-testid="currency-fallback-note">
            Could not load the currencies ScoutAround accepts; showing the default currencies (EUR, USD).
          </p>
        )}
      </div>

      {currency === null && (
        <p id={unknownNoteId} className="text-xs text-amber-700" data-testid="currency-unknown-note">
          {readOnly
            ? 'The partner sent no currency for these prices. Set it at the partner and re-sync.'
            : 'No currency is set for these prices, so this listing cannot be published or synced until one is.'}
        </p>
      )}

      {/* Price on Request toggle */}
      <div className="flex items-center justify-between rounded-md border px-4 py-3">
        <div className="space-y-0.5">
          <Label htmlFor="price-on-request" className="text-sm font-medium">Price on Request</Label>
          <p className="text-xs text-muted-foreground">
            When enabled, visitors must contact the owner for pricing instead of seeing fixed rates.
          </p>
        </div>
        <Switch
          id="price-on-request"
          checked={priceOnRequest}
          onCheckedChange={(checked) => { onChange({ priceOnRequest: checked }); }}
          disabled={readOnly}
          data-testid="price-on-request-switch"
        />
      </div>

      {/* Info banner when prices are auto-calculated */}
      {readOnly && (
        <Alert className="border-blue-200 bg-blue-50/50">
          <Calculator className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-blue-700">
            Prices are auto-calculated from partner pricing using the account&apos;s pricing formula.
            To modify, update the formula in Partner Account settings.
          </AlertDescription>
        </Alert>
      )}

      <div className="grid gap-4 md:grid-cols-3">
        <div className="space-y-2">
          <Label htmlFor="pricePerHour">Price per Hour</Label>
          <div className="relative">
            <Input
              id="pricePerHour"
              type="number"
              min={0}
              step={0.01}
              value={pricePerHour ?? ''}
              onChange={(e) => { handlePriceChange('pricePerHour', e.target.value); }}
              placeholder="0.00"
              className="pr-12"
              data-testid="price-per-hour-input"
              disabled={readOnly}
              readOnly={readOnly}
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
              {currency ?? '—'}
            </span>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="pricePerDay">Price per Day</Label>
          <div className="relative">
            <Input
              id="pricePerDay"
              type="number"
              min={0}
              step={0.01}
              value={pricePerDay ?? ''}
              onChange={(e) => { handlePriceChange('pricePerDay', e.target.value); }}
              placeholder="0.00"
              className="pr-12"
              data-testid="price-per-day-input"
              disabled={readOnly}
              readOnly={readOnly}
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
              {currency ?? '—'}
            </span>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="pricePerWeek">Price per Week</Label>
          <div className="relative">
            <Input
              id="pricePerWeek"
              type="number"
              min={0}
              step={0.01}
              value={pricePerWeek ?? ''}
              onChange={(e) => { handlePriceChange('pricePerWeek', e.target.value); }}
              placeholder="0.00"
              className="pr-12"
              data-testid="price-per-week-input"
              disabled={readOnly}
              readOnly={readOnly}
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
              {currency ?? '—'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
